// turnTraceRecord.js — assemble the FROZEN TurnTrace contract (self-improve-hardening E4-WS1).
//
// Contract (03-PLAN.md, LAW):
//   TurnTrace = { runId, turnId, input, toolCalls, verdicts, ragUsedPct,
//                 costTokens, latencyMs, outcome: 'DONE'|'ABORT' }
//
// This pure helper folds a finished `runLocalAgentLoop` result + session context
// into that record. The `verdicts` + `attempts` it carries are exactly what
// deterministic replay (oracle/replay.js) re-derives the outcome from — so this is
// the WRITE side of the replay contract, kept honest by a round-trip test.
//
// PURE: no I/O, never throws, `now` injectable. `created_at` is intentionally NOT
// stamped here — the worker sets it on persist, so the record stays deterministic.

// stopReasons that mean the turn reached DONE; everything else is an ABORT for the
// DONE|ABORT contract (max_iterations / aborted / context_overflow / etc. surface
// in the bad-runs view as non-clean finishes worth inspecting).
const DONE_STOPS = new Set(['done', 'done_after_budget', 'plan_prose', 'conversational_reply']);

const MAX_INPUT = 4000;
const MAX_TOOLCALLS = 50;
const MAX_EVIDENCE = 2000;
const MAX_VERDICTS = 10;

const clip = (s, n) => String(s ?? '').slice(0, n);

function normNum(v) {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function normVerdicts(verdicts) {
  if (!Array.isArray(verdicts)) return [];
  return verdicts.slice(0, MAX_VERDICTS).map((v) => ({
    verifier: clip(v?.verifier, 16),
    passed: v?.passed === true,
    evidence: clip(v?.evidence, MAX_EVIDENCE),
  }));
}

function normToolCalls(toolCalls) {
  if (!Array.isArray(toolCalls)) return [];
  return toolCalls
    .slice(0, MAX_TOOLCALLS)
    .map((t) => ({ name: clip(typeof t === 'string' ? t : t?.name, 64) }))
    .filter((t) => t.name);
}

function costTokens(usage) {
  if (!usage || typeof usage !== 'object') return 0;
  const tin = Number(usage.input_tokens ?? usage.prompt_tokens ?? 0) || 0;
  const tout = Number(usage.output_tokens ?? usage.completion_tokens ?? 0) || 0;
  return tin + tout;
}

/**
 * @param {{ runId?, turnId?, input?, model?, agentResult?, toolCalls?, startedAt?, now? }} args
 * @returns {{ runId, turnId, model, input, toolCalls, verdicts, attempts, capHit,
 *             reachedGreen, ragUsedPct, faithfulness, ragTotal, weakestStage,
 *             costTokens, latencyMs, iterations, outcome:'DONE'|'ABORT', stopReason }}
 */
export function buildTurnTraceRecord(args) {
  const a = args && typeof args === 'object' ? args : {};
  const r = a.agentResult && typeof a.agentResult === 'object' ? a.agentResult : {};
  const ac = r.autoCorrect && typeof r.autoCorrect === 'object' ? r.autoCorrect : null;
  const rag = r.ragQuality && typeof r.ragQuality === 'object' ? r.ragQuality : null;
  const stopReason = typeof r.stopReason === 'string' ? r.stopReason : null;
  const now = typeof a.now === 'number' ? a.now : Date.now();
  const startedAt = typeof a.startedAt === 'number' ? a.startedAt : null;

  return {
    runId: clip(a.runId, 128) || null,
    turnId: clip(a.turnId, 128) || null,
    model: clip(a.model, 128) || null,
    input: clip(a.input, MAX_INPUT),
    toolCalls: normToolCalls(a.toolCalls),
    verdicts: normVerdicts(ac?.verdicts),
    attempts: Number.isFinite(ac?.attempts) ? ac.attempts : 0,
    capHit: ac?.capHit === true,
    reachedGreen: ac ? ac.reachedGreen === true : null,
    ragUsedPct: rag ? normNum(rag.retrievalUsedPct) : null,
    faithfulness: rag ? normNum(rag.faithfulness) : null,
    ragTotal: rag ? normNum(rag.total) : null,
    weakestStage: r.trace && r.trace.weakestStage ? clip(r.trace.weakestStage.stage, 16) : null,
    costTokens: costTokens(r.usage),
    latencyMs: startedAt != null ? Math.max(0, now - startedAt) : null,
    iterations: Number.isFinite(r.iterations) ? r.iterations : 0,
    outcome: DONE_STOPS.has(stopReason) ? 'DONE' : 'ABORT',
    stopReason,
  };
}
